import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ENVIRONMENT } from 'shared';

/** ViaCarte Balance-Objekt (Konto wie Karte). */
export interface ViacarteBalance {
  pendingBalanceAmount?: number;
  actualBalanceAmount?: number;
  blockedBalanceAmount?: number;
  availableBalanceAmount?: number;
  balanceCurrency?: string;
  error?: string;
}

/** Eine Karte, wie ViaCarte sie liefert (Felder kommen 1:1 aus der API durch). */
export interface ViacarteCard {
  id: string;
  accountId?: string;
  cardType?: number;
  cardTypeLabel?: 'physical' | 'virtual' | 'unknown';
  // ViaCarte liefert KEIN isBlocked im Card-Objekt (nur Eingabe für /card/status),
  // sondern statusNetwork (interner Code). isBlocked bleibt daher meist undefined.
  isBlocked?: boolean;
  statusNetwork?: number;
  status?: string;
  embossName?: string;
  lastFourDigit?: string;
  expiry?: string;
  cardToken?: number | string;
  maskedCardNumber?: string;
  balance?: ViacarteBalance;
  [key: string]: any;
}

/** Eine normalisierte Transaktion (Konto-Bewegung ODER Karten-Transaktion). */
export interface ViacarteTransaction {
  date?: string;
  description?: string;
  amount?: number;
  currency?: string | null;
  pending?: boolean;            // true = autorisiert/gehalten (float), noch nicht gebucht
  kind?: 'account' | 'card';    // account = Transfer/Aufladung, card = Karten-Kauf
  card_type_label?: 'physical' | 'virtual' | 'card'; // nur bei kind='card'
  card_last4?: string;
  id?: string;                       // settled transactionId
  internal_transaction_id?: string;  // float -> zum Posten/Bestätigen einer Pending-Buchung
}

/** Sensible Kartendaten (PAN/CVV) – nur transient, nie speichern. */
export interface ViacarteCardSecureInfo {
  cardNumber?: string;
  cardHolderName?: string;
  expiryDate?: string;
  cvv?: string;
  error?: string;
}

/** Listeneintrag (nur gespeicherte Felder, keine Live-API-Daten). */
export interface ViacarteUserListItem {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  status: string | null;
  status_badge: string;
  brand: string;
  domain_id: number;
  customer_id: string | null;
  preregistration_id: string | null;
  // Top-Level = erstes vorhandenes System (Abwärtskompatibilität).
  account_id: string | null;
  physical_card_id: string | null;
  virtual_card_id: string | null;
  // Account pro ViaCarte-System (fac3less/globalhealth/mnlo). KYC ist user-weit,
  // Account + Karten sind je System getrennt.
  accounts?: Record<string, string | null>;
  registered_at: string | null;
  deleted: boolean;
}

/** Detail = Listeneintrag + defensiv angereicherte Live-Daten der ViaCarte-API. */
export interface ViacarteUserDetail extends ViacarteUserListItem {
  workflow_url: string | null;
  username: string | null;
  mobile: string | null;
  country_code: string | null;
  city: string | null;
  preregistration: any;            // Hash oder { error: string }
  files: any;                      // Array oder { error: string }
  account_balance: ViacarteBalance | null;
  // Balance je System mit Konto (system => Balance). Leere Systeme fehlen.
  account_balances?: Record<string, ViacarteBalance | null>;
  cards: ViacarteCard[] | { error: string };
  transactions?: ViacarteTransaction[];
  recommended_action?: string;
  provision_result?: { system?: string; account_id: string; physical_card_id: string; virtual_card_id: string };
}

/** Ein Währungs-Pocket der Entity-Wallet. */
export interface ViacarteWalletPocket {
  currencyName?: string;
  currencyId?: string;
  balance?: number;
  maxBalanceLimit?: number;
}

/** Guthaben der Entity-Wallet (Quelle der Account-Aufladungen). */
export interface ViacarteEntityWallet {
  totalWalletBalance?: number;
  balance?: ViacarteBalance | null;
  pockets?: ViacarteWalletPocket[] | null;
  error?: string;
}

export interface ViacarteListResponse {
  users: ViacarteUserListItem[];
  total_count: number;
  page: number;
  per_page: number;
  stats: Record<string, number>;
  entity_balance: ViacarteEntityWallet | null;
}

export interface ViacarteListFilters {
  page?: number;
  per_page?: number;
  status?: string;
  search?: string;
}

/**
 * Admin-API-Client für die ViaCarte-Kartenausgabe. Generisch (gleiches Backend
 * für fac3less und Global Health) → lebt in der credo-Lib und wird von beiden
 * Apps genutzt.
 */
@Injectable({ providedIn: 'root' })
export class ViacarteAdminService {
  private readonly apiUrl = this.environment.apiUrl || 'http://localhost:3000/api';

  constructor(private http: HttpClient, @Inject(ENVIRONMENT) private environment: any) {}

  list(filters: ViacarteListFilters = {}): Observable<ViacarteListResponse> {
    let params = new HttpParams();
    if (filters.page)     params = params.set('page', filters.page.toString());
    if (filters.per_page) params = params.set('per_page', filters.per_page.toString());
    if (filters.status)   params = params.set('status', filters.status);
    if (filters.search)   params = params.set('search', filters.search);
    return this.http.get<ViacarteListResponse>(`${this.apiUrl}/admin/viacarte`, { params });
  }

  get(id: number): Observable<ViacarteUserDetail> {
    return this.http.get<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}`);
  }

  refresh(id: number): Observable<ViacarteUserDetail> {
    return this.http.post<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}/refresh`, {});
  }

  /** Provisioniert Account + Karten für ein System (Default fac3less). */
  provision(id: number, currency?: string, system?: string): Observable<ViacarteUserDetail> {
    return this.http.post<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}/provision`, { currency, system });
  }

  /** Geld aus der Entity-Wallet auf den Card-Account des Systems laden. */
  fund(id: number, amount: number, notes?: string, system?: string): Observable<ViacarteUserDetail> {
    return this.http.post<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}/fund`, { amount, notes, system });
  }

  /** Geld vom Card-Account des Systems zurück an die Entity buchen (Cash-out). */
  withdraw(id: number, amount: number, notes?: string, system?: string): Observable<ViacarteUserDetail> {
    return this.http.post<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}/withdraw`, { amount, notes, system });
  }

  /** Guthaben zwischen zwei Karten des Users übertragen (z.B. physisch → virtuell). */
  cardTransfer(id: number, fromCardId: string, toCardId: string, amount: number, notes?: string): Observable<ViacarteUserDetail> {
    return this.http.post<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}/card-transfer`, {
      from_card_id: fromCardId,
      to_card_id: toCardId,
      amount,
      notes
    });
  }

  /** Sensible Kartendaten (PAN/CVV/Expiry) on-demand laden – nicht cachen/speichern. */
  cardSecureInfo(id: number, cardId: string): Observable<ViacarteCardSecureInfo> {
    return this.http.post<ViacarteCardSecureInfo>(`${this.apiUrl}/admin/viacarte/${id}/cards/${cardId}/secure-info`, {});
  }

  /** Sandbox: ausgehende Test-Transaktion auf einer Karte simulieren (post=sofort buchen). */
  simulateTransaction(id: number, cardId: string, amount: number, merchant?: string, post = true): Observable<ViacarteUserDetail> {
    return this.http.post<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}/cards/${cardId}/simulate`, { amount, merchant, post });
  }

  /** Sandbox: eine pending Transaktion nach Settled buchen/bestätigen. */
  postTransaction(id: number, internalTransactionId: string): Observable<ViacarteUserDetail> {
    return this.http.post<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}/transactions/post`, { internal_transaction_id: internalTransactionId });
  }

  /** Karte aktivieren (statusNetwork 1000) via /card/manage-status. */
  activateCard(id: number, cardId: string): Observable<ViacarteUserDetail> {
    return this.http.post<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}/cards/${cardId}/activate`, {});
  }

  /** Karte deaktivieren (statusNetwork 1007). */
  deactivateCard(id: number, cardId: string): Observable<ViacarteUserDetail> {
    return this.http.post<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}/cards/${cardId}/deactivate`, {});
  }

  blockCard(id: number, cardId: string, note?: string): Observable<ViacarteUserDetail> {
    return this.http.post<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}/cards/${cardId}/block`, note ? { note } : {});
  }

  unblockCard(id: number, cardId: string, note?: string): Observable<ViacarteUserDetail> {
    return this.http.post<ViacarteUserDetail>(`${this.apiUrl}/admin/viacarte/${id}/cards/${cardId}/unblock`, note ? { note } : {});
  }
}
